import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrder } from "../../../../api/orderService";
import api from "../../../../api/api";

export default function LegalEntityOrderCreatePage() {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        totalAmount: "",
        currency: "EUR",
        externalOrderNumber: "",
        source: "Web",

        originCountryId: "",
        originCityId: "",
        originPostalCodeId: "",

        destinationCountryId: "",
        destinationCityId: "",
        destinationPostalCodeId: "",
    });

    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [postalCodes, setPostalCodes] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        try {
            const [countriesRes, citiesRes, postalCodesRes] = await Promise.all([
                api.get("/country"),
                api.get("/city"),
                api.get("/postalcode"),
            ]);

            setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : []);
            setCities(Array.isArray(citiesRes.data) ? citiesRes.data : []);
            setPostalCodes(Array.isArray(postalCodesRes.data) ? postalCodesRes.data : []);
        } catch (err) {
            console.error(err);
            setError("Failed to load location data.");
        }
    };

    const handleChange = (e) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const validateRoute = () => {
        return (
            form.originCountryId &&
            form.originCityId &&
            form.originPostalCodeId &&
            form.destinationCountryId &&
            form.destinationCityId &&
            form.destinationPostalCodeId
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!form.totalAmount || Number(form.totalAmount) <= 0) {
            setError("Amount must be greater than 0.");
            return;
        }

        if (!validateRoute()) {
            setError("Please select origin and destination route.");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                totalAmount: Number(form.totalAmount),
                currency: form.currency || "EUR",
                externalOrderNumber: form.externalOrderNumber || null,
                source: form.source || "Web",

                originCountryId: Number(form.originCountryId),
                originCityId: Number(form.originCityId),
                originPostalCodeId: Number(form.originPostalCodeId),

                destinationCountryId: Number(form.destinationCountryId),
                destinationCityId: Number(form.destinationCityId),
                destinationPostalCodeId: Number(form.destinationPostalCodeId),
            };

            const created = await createOrder(payload);

            if (created?.shipmentId) {
                navigate(`/payments/create/${created.shipmentId}`);
            } else {
                navigate("/legal/orders");
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to create order."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">
                Create B2B Order
            </h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <input
                    name="totalAmount"
                    type="number"
                    step="0.01"
                    placeholder="Total amount"
                    value={form.totalAmount}
                    onChange={handleChange}
                    className="border p-2 w-full rounded"
                    required
                />

                <input
                    name="currency"
                    value={form.currency}
                    onChange={handleChange}
                    className="border p-2 w-full rounded"
                />

                <input
                    name="externalOrderNumber"
                    placeholder="External order number"
                    value={form.externalOrderNumber}
                    onChange={handleChange}
                    className="border p-2 w-full rounded"
                />

                <div className="border rounded p-4">
                    <h3 className="font-bold mb-3">Origin</h3>

                    <select
                        name="originCountryId"
                        value={form.originCountryId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded mb-2"
                        required
                    >
                        <option value="">Select origin country</option>
                        {countries.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name || c.countryName}
                            </option>
                        ))}
                    </select>

                    <select
                        name="originCityId"
                        value={form.originCityId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded mb-2"
                        required
                    >
                        <option value="">Select origin city</option>
                        {cities.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name || c.cityName}
                            </option>
                        ))}
                    </select>

                    <select
                        name="originPostalCodeId"
                        value={form.originPostalCodeId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded"
                        required
                    >
                        <option value="">Select origin postal code</option>
                        {postalCodes.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.code || p.postalCode || p.value}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="border rounded p-4">
                    <h3 className="font-bold mb-3">Destination</h3>

                    <select
                        name="destinationCountryId"
                        value={form.destinationCountryId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded mb-2"
                        required
                    >
                        <option value="">Select destination country</option>
                        {countries.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name || c.countryName}
                            </option>
                        ))}
                    </select>

                    <select
                        name="destinationCityId"
                        value={form.destinationCityId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded mb-2"
                        required
                    >
                        <option value="">Select destination city</option>
                        {cities.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name || c.cityName}
                            </option>
                        ))}
                    </select>

                    <select
                        name="destinationPostalCodeId"
                        value={form.destinationPostalCodeId}
                        onChange={handleChange}
                        className="border p-2 w-full rounded"
                        required
                    >
                        <option value="">Select destination postal code</option>
                        {postalCodes.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.code || p.postalCode || p.value}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="submit"
                    className="bg-indigo-600 text-white px-4 py-2 rounded"
                    disabled={loading}
                >
                    {loading ? "Creating..." : "Create Order"}
                </button>
            </form>
        </div>
    );
}